import {Fragment, useEffect, useRef, useState} from 'react';
import {useAllTerminals} from '../../hooks';
import {useTerminalStore} from '../../store';
import {Listbox, Transition} from '@headlessui/react';
import type {TerminalMerchantProps} from "../../model";
import {CheckIcon, ChevronUpDownIcon} from "@heroicons/react/16/solid";

export const TerminalSelector = () => {
    const {terminalId, changeTerminalId} = useTerminalStore();
    const ref = useRef<HTMLInputElement | null>(null)
    const terminals = useAllTerminals();
    const allTerminals = {id: '', name: 'ALL TERMINALS'}

    const [selected, setSelected] = useState(allTerminals)
    const [query, setQuery] = useState('')

    useEffect(() => {
        if (!terminalId) return;
        const terminal = terminals.find(terminal => terminal.id === terminalId)
        !!terminal && setSelected(terminal)
    }, [terminalId, terminals])

    if (terminals.length === 0) {
        return null;
    }

    const filtered =
        query === ''
            ? terminals
            : terminals.filter((terminal) =>
                terminal.name
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .includes(query.toLowerCase().replace(/\s+/g, ''))
            )

    const onChange = (terminal: Pick<TerminalMerchantProps, "id" | "name" | "paymentFlow">) => {
        setSelected(terminal)
        changeTerminalId(terminal.id)
    };

    return (
        <div className="flex gap-2 items-center relative !outline-none">
            <span>Terminal:</span>
            <Listbox
                value={selected}
                onChange={onChange}
            >
                <div className="relative mt-1">
                    <div
                        className="relative flex p-2 w-72 cursor-default overflow-hidden rounded-lg bg-white text-left shadow-md sm:text-sm">
                        <Listbox.Button className="w-full text-left outline-none">{selected?.name}</Listbox.Button>
                        <ChevronUpDownIcon
                            className="h-5 w-5 text-gray-400 absolute right-0"
                            aria-hidden="true"
                        />
                    </div>
                </div>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterEnter={() => {
                        ref.current?.focus();
                    }}
                    afterLeave={() => setQuery('')}
                >
                    <Listbox.Options
                        className="absolute w-72 z-50 mt-1 max-h-60 top-[40px] right-0 overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-none focus:outline-none sm:text-sm">
                        <div className="m-1">
                            <input
                                ref={ref}
                                className="border-2 border-brand-1 w-full mx-auto py-2 px-10 text-sm leading-5 rounded-md outline-brand-1 text-gray-900 focus:ring-0"
                                onChange={(event) => setQuery(event.target.value)}
                            />
                        </div>
                        {filtered.length === 0 && query !== '' ? (
                            <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                                Nothing found.
                            </div>
                        ) : (
                            [allTerminals, ...filtered].map((person) => (
                                <Listbox.Option
                                    key={person.id}
                                    value={person}
                                    className={({active}) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 text-xs ${
                                            active ? 'bg-brand-1 text-white' : 'text-gray-900'
                                        }`
                                    }
                                >
                                    {({selected, active}) => (
                                        <>
                                            <span
                                                className={`block truncate ${
                                                    selected ? 'font-medium' : 'font-normal'
                                                }`}
                                            >
                                              {person.name}
                                            </span>
                                            {selected ? (
                                                <span
                                                    className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                                        active ? 'text-white' : 'text-brand-1'
                                                    }`}
                                                >
                                                    <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                                                  </span>
                                            ) : null}
                                        </>
                                    )}
                                </Listbox.Option>
                            )))}
                    </Listbox.Options>
                </Transition>
            </Listbox>
        </div>
    );
};
